"use client";

/**
 * IntercomFacade - Lightweight chat button placeholder
 *
 * Shows a CSS-only chat button before Intercom loads.
 * When clicked, triggers Intercom to load and show.
 * Automatically hides once real Intercom launcher appears.
 *
 * CRITICAL: Dimensions and POSITIONING must EXACTLY match Intercom's launcher to prevent layout shift
 * Intercom launcher: 48x48px visible, positioned bottom:20px right:20px (desktop), bottom:70px right:20px (mobile)
 * See globals.css for mobile positioning override on real Intercom launcher
 */
import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { getLastChatOpenRequest, LAUNCHER_SELECTOR, useIntercom, type ChatOpenSource } from "./IntercomProvider";

// ============================================================================
// support_opened (H35 T7): one PostHog event each time the messenger opens. Tracking only: nothing here opens,
// closes or delays the messenger, and the analytics chunk is imported only when an open happens.
//   trigger              "user" when the visitor asked for it (our chat button and its show() call, an openIntercomChat
//                        link, Intercom's own launcher), "auto" for anything else (workspace auto-message, Fin, a reply
//                        arriving, a restored state), even right after a click on some other button or link (the card
//                        form's pay button)
//   source               on a user open from an openIntercomChat link: "help", "cancel" or "upgrade" when known, else null
//   path, setup_step     page path; the signup step (?step=, 0 when absent) on /setup, else null
//   over_card_form       a payment-form iframe (Evervault card fields) is on screen when it opens
//   auto_over_card_form  it opened on its own over that form (H14: chat auto-opened over the desktop card form)
// ============================================================================

const USER_OPEN_WINDOW_MS = 2000;
// A facade click loads the widget first, so its open can arrive seconds later (slow networks: up to the 10 s boot wait).
const FACADE_OPEN_WINDOW_MS = 15000;
const CARD_FORM_SELECTOR = 'iframe[src*="evervault"], [data-evervault]';
const SETUP_PATH_RE = /^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?(?:app\/)?setup(?:\/|$)/;
let userRequestAt = 0;
let facadeRequestUntil = 0;
let facadeRequestAt = 0;
let frameFocusAt = 0;
let supportTrackingRegistered = false;
type SupportSink = (event: string, props: Record<string, unknown>) => void;
const posthogSink: SupportSink = (event, props) => {
  import("@/lib/analytics/posthog-client").then(m => m.phCapture(event, props)).catch(() => {});
};
let supportSink: SupportSink = posthogSink;
/** Tests only: replaces the PostHog sink and clears the module state (null restores the sink). */
export function resetSupportTrackingForTests(sink: SupportSink | null): void {
  supportSink = sink ?? posthogSink;
  userRequestAt = 0;
  facadeRequestUntil = 0;
  facadeRequestAt = 0;
  frameFocusAt = 0;
  supportTrackingRegistered = false;
}

/** Our facade button was pressed: the open that follows (after the widget loads) is the visitor's. */
function markFacadeOpenRequest(): void {
  facadeRequestAt = Date.now();
  facadeRequestUntil = facadeRequestAt + FACADE_OPEN_WINDOW_MS;
}
function activeIsIntercomFrame(doc: Document): boolean {
  const el = doc.activeElement;
  return !!el && el.tagName === "IFRAME" && /^intercom-/.test((el as HTMLIFrameElement).name || "");
}
function cardFormOnScreen(win: Window): boolean {
  const vw = win.innerWidth;
  const vh = win.innerHeight;
  return Array.from(win.document.querySelectorAll(CARD_FORM_SELECTOR)).some(el => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
  });
}

/** Properties of one support_opened event (pure). */
export function supportOpenedProps(input: {
  pathname: string;
  search: string;
  userRequested: boolean;
  overCardForm: boolean;
  source?: ChatOpenSource;
}) {
  let setupStep: number | null = null;
  if (SETUP_PATH_RE.test(input.pathname)) {
    const step = Number.parseInt(new URLSearchParams(input.search).get("step") ?? "0", 10);
    setupStep = Number.isInteger(step) && step >= 0 ? step : 0;
  }
  const trigger = input.userRequested ? "user" : "auto";
  return {
    trigger,
    source: trigger === "user" ? input.source ?? null : null,
    path: input.pathname,
    setup_step: setupStep,
    over_card_form: input.overCardForm,
    auto_over_card_form: trigger === "auto" && input.overCardForm
  };
}

/**
 * Registers the onShow listener once per page (Intercom keeps onShow callbacks for the page's life, and the landing
 * and app layouts each mount a facade). It works on the provider's queueing stub too: the widget runs the queued
 * onShow before boot, so the first open after a lazy load is seen. A press, click or Enter/Space on a chat launcher in
 * this document, or focus moving into an Intercom frame (its launcher lives in one), in the 2 s before the open marks
 * it as the visitor's; so do our facade's show() (markFacadeOpenRequest) and an openIntercomChat call (the provider's
 * getLastChatOpenRequest, which also names the link). Returns whether it registered now.
 */
export function registerSupportOpenTracking(win: Window & typeof globalThis = window): boolean {
  if (supportTrackingRegistered || typeof win.Intercom !== "function") return false;
  supportTrackingRegistered = true;
  const doc = win.document;
  const onPointer = (event: Event) => {
    const target = event.target;
    if (target instanceof win.Element && target.closest(LAUNCHER_SELECTOR)) userRequestAt = Date.now();
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    onPointer(event);
  };
  // Clicks inside Intercom's frames never reach this window; focus moving into one blurs it.
  const onBlur = () => {
    frameFocusAt = Date.now();
  };
  win.addEventListener("pointerdown", onPointer, {
    capture: true,
    passive: true
  });
  win.addEventListener("click", onPointer, {
    capture: true,
    passive: true
  });
  win.addEventListener("keydown", onKey, {
    capture: true,
    passive: true
  });
  win.addEventListener("blur", onBlur);
  (win.Intercom as (cmd: string, ...a: unknown[]) => void)("onShow", () => {
    try {
      const now = Date.now();
      const chat = getLastChatOpenRequest();
      let userRequested = false;
      let source: ChatOpenSource = null;
      if (now <= facadeRequestUntil) {
        userRequested = true;
        facadeRequestUntil = 0;
        // Before the widget loads, openIntercomChat opens the chat by clicking our facade.
        if (chat.at > 0 && Math.abs(facadeRequestAt - chat.at) <= 1000) source = chat.source;
      } else if (chat.at > 0 && now - chat.at <= USER_OPEN_WINDOW_MS) {
        userRequested = true;
        source = chat.source;
      } else if (now - userRequestAt <= USER_OPEN_WINDOW_MS) {
        userRequested = true;
      } else if (now - frameFocusAt <= USER_OPEN_WINDOW_MS && activeIsIntercomFrame(doc)) {
        userRequested = true;
      }
      supportSink("support_opened", supportOpenedProps({
        pathname: win.location.pathname,
        search: win.location.search,
        userRequested,
        overCardForm: cardFormOnScreen(win),
        source
      }));
    } catch {
      // Tracking never throws into Intercom.
    }
  });
  return true;
}
interface IntercomFacadeProps {
  className?: string;
}

// Helper to check for Intercom launcher elements
function checkForIntercomLauncher(): boolean {
  return !!(document.querySelector(".intercom-lightweight-app-launcher") || document.querySelector(".intercom-launcher-frame") || document.querySelector(".intercom-launcher") || document.querySelector('iframe[name="intercom-launcher-frame"]'));
}
export function IntercomFacade(t0) {
  const $ = _c(22);
  const {
    className
  } = t0;
  const {
    show,
    isLoaded,
    isLoading
  } = useIntercom();
  const [isHidden, setIsHidden] = useState(false);
  let t1;
  let t2;
  if ($[0] !== isHidden || $[1] !== isLoading) {
    t1 = () => {
      if (!isLoading || isHidden) {
        return;
      }
      if (checkForIntercomLauncher()) {
        setIsHidden(true);
        return;
      }
      const interval = setInterval(() => {
        if (checkForIntercomLauncher()) {
          setIsHidden(true);
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    };
    t2 = [isLoading, isHidden];
    $[0] = isHidden;
    $[1] = isLoading;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== isHidden || $[5] !== isLoaded) {
    t3 = () => {
      if (!isLoaded || isHidden) {
        return;
      }
      if (checkForIntercomLauncher()) {
        setIsHidden(true);
        return;
      }
      const interval_0 = setInterval(() => {
        if (checkForIntercomLauncher()) {
          setIsHidden(true);
          clearInterval(interval_0);
        }
      }, 100);
      const timeout = setTimeout(() => {
        clearInterval(interval_0);
        setIsHidden(true);
      }, 2000);
      return () => {
        clearInterval(interval_0);
        clearTimeout(timeout);
      };
    };
    t4 = [isLoaded, isHidden];
    $[4] = isHidden;
    $[5] = isLoaded;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== isLoaded || $[9] !== isLoading) {
    t5 = [isLoaded, isLoading];
    $[8] = isLoaded;
    $[9] = isLoading;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(_temp, t5);
  let t6;
  if ($[11] !== show) {
    t6 = () => {
      markFacadeOpenRequest();
      show();
    };
    $[11] = show;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleClick = t6;
  if (isHidden) {
    return null;
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <style>{"\n                @media (max-width: 640px) {\n                    html[data-demo] #intercom-facade-btn {\n                        bottom: 70px !important;\n                    }\n                }\n                @keyframes intercom-facade-spin { to { transform: rotate(360deg); } }\n            "}</style>;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: 2147483000,
      width: "48px",
      height: "48px",
      padding: 0,
      margin: 0,
      borderRadius: "50%",
      backgroundColor: "#141414",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "rgba(0, 0, 0, 0.06) 0px 1px 6px 0px, rgba(0, 0, 0, 0.16) 0px 2px 32px 0px",
      transition: "transform 0.15s ease, box-shadow 0.15s ease"
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isLoading) {
    t9 = isLoading ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{
      animation: "intercom-facade-spin 1s linear infinite"
    }}><circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" fill="none" /><path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" /></svg> : <svg width="24" height="24" viewBox="0 0 28 32" fill="white"><path d="M28,32 C28,32 23.2863266,30.1450667 19.4727818,28.6592 L3.43749107,28.6592 C1.53921989,28.6592 0,27.0272 0,25.0144 L0,3.6448 C0,1.632 1.53921989,0 3.43749107,0 L24.5615088,0 C26.45978,0 27.9989999,1.632 27.9989999,3.6448 L27.9989999,22.0490667 L28,22.0490667 L28,32 Z M23.8614088,20.0181333 C23.5309223,19.6105242 22.9540812,19.5633836 22.5692242,19.9125333 C22.5392199,19.9392 19.5537934,22.5941333 13.9989999,22.5941333 C8.51321617,22.5941333 5.48178311,19.9584 5.4277754,19.9104 C5.04295119,19.5629428 4.46760991,19.6105095 4.13759108,20.0170667 C3.97913051,20.2124916 3.9004494,20.4673395 3.91904357,20.7249415 C3.93763774,20.9825435 4.05196575,21.2215447 4.23660523,21.3888 C4.37862552,21.5168 7.77411059,24.5386667 13.9989999,24.5386667 C20.2248893,24.5386667 23.6203743,21.5168 23.7623946,21.3888 C23.9467342,21.2215726 24.0608642,20.9827905 24.0794539,20.7254507 C24.0980436,20.4681109 24.0195551,20.2135019 23.8614088,20.0181333 Z" /></svg>;
    $[15] = isLoading;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== className || $[18] !== handleClick || $[19] !== isLoading || $[20] !== t9) {
    t10 = <>{t7}<button id="intercom-facade-btn" onClick={handleClick} disabled={isLoading} aria-label="Open chat support" className={className} style={t8} onMouseEnter={_temp2} onMouseLeave={_temp3}>{t9}</button></>;
    $[17] = className;
    $[18] = handleClick;
    $[19] = isLoading;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp3(e_0) {
  e_0.currentTarget.style.transform = "scale(1)";
}
function _temp2(e) {
  e.currentTarget.style.transform = "scale(1.08)";
}
function _temp() {
  if (typeof window !== "undefined") {
    registerSupportOpenTracking();
  }
}